import { useEffect } from 'react'
import {
  Box,
  capitalize,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { CloseIcon, CorrectIcon, ManagerIcon } from 'src/v3/core/icons'
import { RecertCycleDetail, RecertCycleDetailItem } from 'src/v3/core/types'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchRecertCycleDetail,
  selectRecertCycleDetail,
  selectRecertCycleDetailStatus,
} from 'src/v3/manage/compliance.slice'

type Props = {
  cycleID: string | null
  cycleName: string | null
  onClose: () => void
}

function statusColor(
  status: string | null,
): 'default' | 'primary' | 'success' | 'error' {
  if (status === 'completed') return 'success'
  if (status === 'started' || status === 'restarted') return 'primary'
  return 'default'
}

function ItemRow({ item }: { item: RecertCycleDetailItem }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 0.4,
      }}
    >
      {item.isComplete ? (
        <CorrectIcon
          sx={{ fontSize: 13, color: 'success.main', flexShrink: 0 }}
        />
      ) : (
        <Box
          sx={{
            width: 13,
            height: 13,
            borderRadius: '50%',
            border: '1.5px solid',
            borderColor: 'text.disabled',
            flexShrink: 0,
          }}
        />
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          flex: 1,
          minWidth: 0,
        }}
      >
        {item.required && (
          <Tooltip title="Required by manager">
            <ManagerIcon
              sx={{ fontSize: 13, color: 'secondary.main', flexShrink: 0 }}
            />
          </Tooltip>
        )}
        <Typography variant="caption" sx={{ lineHeight: 1.3 }}>
          {item.name}
        </Typography>
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ flexShrink: 0, ml: 1 }}
      >
        {item.hours.toFixed(1)}h
      </Typography>
    </Box>
  )
}

function ItemSection({
  items,
  label,
}: {
  items: RecertCycleDetailItem[]
  label: string
}) {
  if (items.length === 0) return null
  return (
    <Box sx={{ mt: 1 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          fontSize: 10,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ mt: 0.25 }}>
        {items.map((item) => (
          <ItemRow key={item.id} item={item} />
        ))}
      </Box>
    </Box>
  )
}

function CategoryBlock({
  cat,
}: {
  cat: RecertCycleDetail['categories'][number]
}) {
  let pct =
    cat.requiredHours > 0
      ? Math.min(
          100,
          Math.round((cat.completedHours / cat.requiredHours) * 100),
        )
      : 0

  let hasItems =
    cat.courses.length > 0 ||
    cat.liveEvents.length > 0 ||
    cat.assignedEvents.length > 0 ||
    cat.externalCertificates.length > 0

  return (
    <Box sx={{ py: 1.5 }}>
      {/* Category header */}
      <Box className="flex items-center gap-1 mb-1">
        <Box className="flex items-center gap-1 flex-1 min-w-0">
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
            {cat.name}
          </Typography>
          {cat.subcategory && (
            <Typography variant="caption" color="text.secondary" noWrap>
              · {cat.subcategory}
            </Typography>
          )}
          {cat.categoryType === 'live' && (
            <Chip
              label="Live"
              size="small"
              variant="outlined"
              sx={{ ml: 0.5, height: 16, fontSize: 10 }}
            />
          )}
        </Box>
        <Box className="flex items-center gap-2 shrink-0">
          <Typography variant="caption" color="text.secondary">
            {cat.completedHours.toFixed(1)} / {cat.requiredHours.toFixed(1)} hrs
          </Typography>
          <Chip
            label={cat.isComplete ? 'Complete' : `${pct}%`}
            color={cat.isComplete ? 'success' : 'default'}
            size="small"
          />
        </Box>
      </Box>

      <LinearProgress
        variant="determinate"
        value={pct}
        color={cat.isComplete ? 'success' : 'primary'}
        sx={{ height: 5, borderRadius: 3, mb: hasItems ? 0.5 : 0 }}
      />

      {hasItems && (
        <Box sx={{ ml: 0.5 }}>
          <ItemSection items={cat.courses} label="Courses" />
          <ItemSection items={cat.liveEvents} label="Live Classes" />
          <ItemSection items={cat.assignedEvents} label="Assigned Events" />
          <ItemSection
            items={cat.externalCertificates}
            label="External Certificates"
          />
        </Box>
      )}
    </Box>
  )
}

export default function RecertCycleDetailModal({
  cycleID,
  cycleName,
  onClose,
}: Props) {
  let dispatch = useAppDispatch()
  let detail =
    useAppSelector(
      cycleID ? selectRecertCycleDetail(cycleID) : () => undefined,
    ) ?? null
  let status = useAppSelector(
    cycleID ? selectRecertCycleDetailStatus(cycleID) : () => undefined,
  )
  let loading = status === 'pending' || (!!cycleID && !status)

  useEffect(() => {
    if (cycleID && (!status || status === 'uninitialized')) {
      dispatch(fetchRecertCycleDetail(cycleID))
    }
  }, [cycleID, status, dispatch])

  let overallPct =
    detail && detail.totalRequiredHours > 0
      ? Math.min(
          100,
          Math.round(
            (detail.totalCompletedHours / detail.totalRequiredHours) * 100,
          ),
        )
      : 0

  return (
    <Dialog open={!!cycleID} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6, pb: 1 }}>
        <Typography
          variant="subtitle1"
          component="span"
          sx={{ fontWeight: 600 }}
        >
          {cycleName ?? 'Recertification Cycle'}
        </Typography>
        <IconButton
          size="small"
          aria-label="Close"
          onClick={onClose}
          sx={{ position: 'absolute', top: 12, right: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0, overflowY: 'auto', maxHeight: '70vh' }}>
        {loading && (
          <Box className="flex justify-center py-8">
            <CircularProgress size={32} />
          </Box>
        )}

        {!loading && detail && (
          <>
            {/* Status + date strip */}
            <Box className="flex items-center gap-2 flex-wrap mb-3">
              <Chip
                label={
                  detail.status ? capitalize(detail.status) : 'Not Started'
                }
                color={statusColor(detail.status)}
                size="small"
              />
              <Typography variant="caption" color="text.secondary">
                {dayjs(detail.issueDate).format('MMM D, YYYY')} –{' '}
                {dayjs(detail.expirationDate).format('MMM D, YYYY')}
              </Typography>
            </Box>

            {/* Overall progress card */}
            <Box
              sx={{
                bgcolor: 'background.paperDark',
                borderRadius: 1,
                p: 2,
                mb: 3,
              }}
            >
              <Box className="flex items-center justify-between mb-1">
                <Typography variant="body2" color="text.secondary">
                  Overall Progress
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {detail.totalCompletedHours.toFixed(1)} /{' '}
                  {detail.totalRequiredHours.toFixed(1)} hrs
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={overallPct}
                color={overallPct === 100 ? 'success' : 'primary'}
                sx={{ height: 10, borderRadius: 5, mb: 1 }}
              />
              <Typography variant="caption" color="text.secondary">
                {overallPct}% complete ·{' '}
                {detail.categories.filter((c) => c.isComplete).length}/
                {detail.categories.length} categories done
              </Typography>
            </Box>

            {/* Category breakdown */}
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Categories
            </Typography>

            {detail.categories.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No categories configured.
              </Typography>
            ) : (
              detail.categories
                .toSorted((a, b) => {
                  if (a.categoryType === 'live') return 1
                  if (b.categoryType === 'live') return -1
                  return a.name.localeCompare(b.name)
                })
                .map((cat, i) => (
                  <Box key={cat.id}>
                    <CategoryBlock cat={cat} />
                    {i < detail.categories.length - 1 && <Divider />}
                  </Box>
                ))
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
